import React, { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../../api/client';
import { useAuth } from '../../contexts/AuthContext';
import { CreateOrderForm } from './CreateOrderForm';
import { OrdersList } from './OrdersList';
import { ImportOrdersSection } from '../order-import/ImportOrdersSection';
import { DataManagementTab } from './ticket-inventory/DataManagementTab';
import { OrderTicketAudit } from './ticket-inventory/OrderTicketAudit';
import { ReconciliationTab } from './ticket-inventory/ReconciliationTab';
import { PRIMARY, TEXT_MUTED, BORDER_LIGHT } from './table-management/designTokens';

type TicketSubView = 'dashboard' | 'tickets' | 'orders' | 'import' | 'audit' | 'data-management' | 'reconciliation';

const TICKET_SUB_VIEWS: { key: TicketSubView; label: string; organizerOnly?: boolean }[] = [
  { key: 'dashboard', label: 'Dashboard' },
  { key: 'tickets', label: 'Tickets' },
  { key: 'orders', label: 'Orders' },
  { key: 'import', label: 'Import' },
  { key: 'audit', label: 'Order/Ticket Audit', organizerOnly: true },
  { key: 'data-management', label: 'Data Management', organizerOnly: true },
  { key: 'reconciliation', label: 'Reconciliation', organizerOnly: true },
];

interface Tier {
  id: string;
  name: string;
  price: number;
  active: boolean;
}

interface TierStats {
  tierName: string;
  count: number;
  revenue: number;
}

interface TicketInventoryStats {
  totalCount: number;
  assignedCount: number;
  unassignedCount: number;
  reconciledCount: number;
  totalRevenue: number;
  complimentary?: {
    ticketCount: number;
    faceValue: number;
  };
  byTier: TierStats[];
}

interface TicketRow {
  id: string;
  ticketNumber: number;
  tierName: string;
  priceAtPurchase: number;
  status: string;
  reconciled: boolean;
  reconciledAt: string | null;
  assignedTo: string | null;
  registrationId: string | null;
  createdVia: string | null;
  createdAt: string;
}

interface TicketInventoryResponse {
  stats: TicketInventoryStats;
  tickets: TicketRow[];
}

interface BulkGenerateResponse {
  created: number;
  tierName: string;
}

const styles: Record<string, React.CSSProperties> = {
  container: { marginTop: '0.5rem' },
  loading: {
    textAlign: 'center' as const,
    padding: '2rem',
    color: '#666',
    fontSize: '1rem',
  },
  error: {
    color: '#d32f2f',
    backgroundColor: '#fdecea',
    padding: '0.75rem 1rem',
    borderRadius: '4px',
    marginBottom: '1rem',
    fontSize: '0.85rem',
  },
  statsPanel: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
    gap: '0.75rem',
    marginBottom: '1.5rem',
  },
  statCard: {
    backgroundColor: '#f9fafb',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '1rem',
    textAlign: 'center' as const,
  },
  statLabel: {
    fontSize: '0.75rem',
    fontWeight: 500,
    color: '#6b7280',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
    marginBottom: '0.25rem',
    display: 'block',
  },
  statValue: {
    fontSize: '1.5rem',
    fontWeight: 700,
    color: '#0f2d1f',
  },
  sectionTitle: {
    fontSize: '0.95rem',
    fontWeight: 600,
    color: '#0f2d1f',
    marginBottom: '0.75rem',
    marginTop: '1rem',
  },
  tierGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '0.5rem',
    marginBottom: '1.5rem',
  },
  tierCard: {
    backgroundColor: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '0.75rem 1rem',
  },
  tierName: {
    fontSize: '0.85rem',
    fontWeight: 600,
    color: '#374151',
    marginBottom: '0.25rem',
  },
  tierDetail: {
    fontSize: '0.8rem',
    color: '#6b7280',
  },
  formSection: {
    backgroundColor: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '1.25rem',
    marginBottom: '1.5rem',
  },
  formTitle: {
    fontSize: '0.95rem',
    fontWeight: 600,
    color: '#0f2d1f',
    marginBottom: '1rem',
    marginTop: 0,
  },
  formRow: {
    display: 'flex',
    gap: '0.75rem',
    alignItems: 'flex-end',
    flexWrap: 'wrap' as const,
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.25rem',
  },
  formLabel: {
    fontSize: '0.8rem',
    fontWeight: 500,
    color: '#374151',
  },
  formSelect: {
    padding: '0.5rem 0.75rem',
    border: '1px solid #d1d5db',
    borderRadius: '4px',
    fontSize: '0.85rem',
    minWidth: '180px',
    backgroundColor: '#fff',
  },
  formInput: {
    padding: '0.5rem 0.75rem',
    border: '1px solid #d1d5db',
    borderRadius: '4px',
    fontSize: '0.85rem',
    width: '100px',
  },
  formButton: {
    padding: '0.5rem 1rem',
    backgroundColor: '#0f2d1f',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    fontSize: '0.85rem',
    fontWeight: 500,
    cursor: 'pointer',
  },
  formButtonDisabled: {
    padding: '0.5rem 1rem',
    backgroundColor: '#9ca3af',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    fontSize: '0.85rem',
    fontWeight: 500,
    cursor: 'not-allowed',
  },
  successMessage: {
    color: '#166534',
    backgroundColor: '#dcfce7',
    padding: '0.5rem 0.75rem',
    borderRadius: '4px',
    fontSize: '0.85rem',
    marginTop: '0.75rem',
  },
  formError: {
    color: '#d32f2f',
    backgroundColor: '#fdecea',
    padding: '0.5rem 0.75rem',
    borderRadius: '4px',
    fontSize: '0.85rem',
    marginTop: '0.75rem',
  },
  tableContainer: {
    marginTop: '1.5rem',
    overflowX: 'auto' as const,
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    fontSize: '0.85rem',
  },
  tableHeader: {
    backgroundColor: '#f9fafb',
    borderBottom: '2px solid #e5e7eb',
  },
  th: {
    padding: '0.6rem 0.75rem',
    textAlign: 'left' as const,
    fontWeight: 600,
    color: '#374151',
    fontSize: '0.75rem',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
  },
  td: {
    padding: '0.6rem 0.75rem',
    borderBottom: '1px solid #e5e7eb',
    color: '#374151',
  },
  unassignedBadge: {
    display: 'inline-block',
    backgroundColor: '#f3f4f6',
    color: '#6b7280',
    fontStyle: 'italic' as const,
    fontSize: '0.8rem',
    padding: '0.15rem 0.5rem',
    borderRadius: '4px',
  },
  reconcileCheckbox: {
    width: '1rem',
    height: '1rem',
    cursor: 'pointer',
    accentColor: '#0f2d1f',
  },
  reconcileError: {
    color: '#d32f2f',
    backgroundColor: '#fdecea',
    padding: '0.5rem 0.75rem',
    borderRadius: '4px',
    fontSize: '0.8rem',
    marginBottom: '0.75rem',
  },
  subNav: {
    display: 'flex',
    gap: '0',
    marginBottom: '1rem',
    borderBottom: `1px solid ${BORDER_LIGHT}`,
  },
  subTab: {
    padding: '0.5rem 1rem',
    fontSize: '0.85rem',
    fontWeight: 500,
    cursor: 'pointer',
    border: 'none',
    backgroundColor: 'transparent',
    color: TEXT_MUTED,
    borderBottom: '2px solid transparent',
    marginBottom: '-1px',
    transition: 'color 0.2s, border-color 0.2s',
  },
  subTabActive: {
    padding: '0.5rem 1rem',
    fontSize: '0.85rem',
    fontWeight: 600,
    cursor: 'pointer',
    border: 'none',
    backgroundColor: 'transparent',
    color: PRIMARY,
    borderBottom: `2px solid ${PRIMARY}`,
    marginBottom: '-1px',
    transition: 'color 0.2s, border-color 0.2s',
  },
  ticketToolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    marginBottom: '0.75rem',
    flexWrap: 'wrap' as const,
  },
  toolbarSelect: {
    padding: '0.4rem 0.75rem',
    fontSize: '0.85rem',
    border: '1px solid #d1d5db',
    borderRadius: '4px',
    backgroundColor: '#fff',
    color: '#374151',
    cursor: 'pointer',
  },
  toolbarToggle: {
    padding: '0.35rem 0.75rem',
    fontSize: '0.8rem',
    fontWeight: 500,
    border: '1px solid #d1d5db',
    borderRadius: '4px',
    backgroundColor: '#fff',
    color: '#374151',
    cursor: 'pointer',
    transition: 'background-color 0.15s, border-color 0.15s',
  },
  toolbarToggleActive: {
    padding: '0.35rem 0.75rem',
    fontSize: '0.8rem',
    fontWeight: 600,
    border: `1px solid ${PRIMARY}`,
    borderRadius: '4px',
    backgroundColor: '#e8f5e9',
    color: PRIMARY,
    cursor: 'pointer',
    transition: 'background-color 0.15s, border-color 0.15s',
  },
  collapseHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer',
    userSelect: 'none' as const,
  },
  collapseArrow: {
    fontSize: '0.75rem',
    color: '#6b7280',
    transition: 'transform 0.2s',
  },
  tierGroupHeader: {
    padding: '0.6rem 0.75rem',
    backgroundColor: '#f3f4f6',
    fontWeight: 600,
    fontSize: '0.85rem',
    color: '#0f2d1f',
    borderBottom: '1px solid #e5e7eb',
  },
};

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
}

export function TicketInventoryTab() {
  const { user } = useAuth();
  const [activeView, setActiveView] = useState<TicketSubView>('dashboard');
  const [data, setData] = useState<TicketInventoryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Bulk generate form state
  const [tiers, setTiers] = useState<Tier[]>([]);
  const [selectedTierId, setSelectedTierId] = useState('');
  const [quantity, setQuantity] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [formError, setFormError] = useState('');
  const [ordersRefreshKey, setOrdersRefreshKey] = useState(0);

  // Reconcile state
  const [reconcileError, setReconcileError] = useState('');

  // Tickets view: filter, group, collapse
  const [tierFilter, setTierFilter] = useState('');
  const [createdViaFilter, setCreatedViaFilter] = useState('');
  const [ticketSearch, setTicketSearch] = useState('');
  const [groupByTier, setGroupByTier] = useState(false);
  const [ticketsCollapsed, setTicketsCollapsed] = useState(false);

  const fetchInventory = useCallback(async () => {
    try {
      const result = await apiClient<TicketInventoryResponse>(
        '/legacy-ball/ticket-inventory',
      );
      setData(result);
    } catch {
      setError('Could not load ticket inventory.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchInventory();
  }, [fetchInventory]);

  // Fetch active tiers for the dropdown
  useEffect(() => {
    async function fetchTiers() {
      try {
        const allTiers = await apiClient<Tier[]>('/legacy-ball/tiers');
        setTiers(allTiers.filter((t) => t.active));
      } catch {
        // Silently fail — form will show empty dropdown
      }
    }
    fetchTiers();
  }, []);

  const handleBulkGenerate = async (e: React.FormEvent) => {
    e.preventDefault();
    setSuccessMessage('');
    setFormError('');

    const qty = parseInt(quantity, 10);
    if (!selectedTierId) {
      setFormError('Please select a tier.');
      return;
    }
    if (isNaN(qty) || qty < 1 || qty > 500) {
      setFormError('Quantity must be between 1 and 500.');
      return;
    }

    setSubmitting(true);
    try {
      const result = await apiClient<BulkGenerateResponse>(
        '/legacy-ball/ticket-inventory/bulk-generate',
        {
          method: 'POST',
          body: { tierId: selectedTierId, quantity: qty },
        },
      );
      setSuccessMessage(
        `Generated ${result.created} ${result.tierName} tickets`,
      );
      setQuantity('');
      setSelectedTierId('');
      // Re-fetch inventory to update stats and table
      await fetchInventory();
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setFormError(apiErr?.message || 'Failed to generate tickets.');
    } finally {
      setSubmitting(false);
    }
  };

  // Ticket delete impact state
  const [deleteImpact, setDeleteImpact] = useState<any>(null);
  const [deleteImpactLoading, setDeleteImpactLoading] = useState(false);
  const [deletingTicket, setDeletingTicket] = useState(false);

  const handleDeleteTicket = async (ticketId: string, ticketNumber: number, assignedTo: string | null) => {
    // Step 1: Fetch impact analysis
    setDeleteImpactLoading(true);
    setDeleteImpact(null);
    try {
      const impact = await apiClient<any>(`/order-data-management/tickets/${ticketId}/pre-delete-check`);
      setDeleteImpact(impact);
    } catch (err: any) {
      alert(err?.message || 'Failed to check ticket dependencies');
    }
    setDeleteImpactLoading(false);
  };

  const confirmDeleteTicket = async () => {
    if (!deleteImpact) return;
    setDeletingTicket(true);
    try {
      await apiClient(`/order-data-management/tickets/${deleteImpact.ticketId}`, { method: 'DELETE' });
      setData((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          tickets: prev.tickets.filter((t) => t.id !== deleteImpact.ticketId),
          stats: { ...prev.stats, totalCount: prev.stats.totalCount - 1 },
        };
      });
      setDeleteImpact(null);
    } catch (err: any) {
      alert(err?.message || 'Failed to delete ticket');
    }
    setDeletingTicket(false);
  };

  const handleChangeTier = async (ticketId: string, newTierId: string) => {
    const tier = tiers.find((t) => t.id === newTierId);
    if (!tier) return;
    try {
      await apiClient(`/order-data-management/tickets/${ticketId}/tier`, {
        method: 'PATCH',
        body: { newTierId },
      });
      // Update local state
      setData((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          tickets: prev.tickets.map((t) =>
            t.id === ticketId ? { ...t, tierName: tier.name, priceAtPurchase: tier.price } : t,
          ),
        };
      });
    } catch (err: any) {
      alert(err?.message || 'Failed to change tier');
    }
  };

  const handleReconcileToggle = async (ticketId: string, newValue: boolean) => {
    setReconcileError('');

    // Optimistic update
    setData((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        tickets: prev.tickets.map((t) =>
          t.id === ticketId
            ? { ...t, reconciled: newValue, reconciledAt: newValue ? new Date().toISOString() : null }
            : t,
        ),
        stats: {
          ...prev.stats,
          reconciledCount: prev.stats.reconciledCount + (newValue ? 1 : -1),
        },
      };
    });

    try {
      await apiClient(`/legacy-ball/tickets/${ticketId}/reconcile`, {
        method: 'PATCH',
        body: { reconciled: newValue },
      });
    } catch {
      // Revert optimistic update on error
      setData((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          tickets: prev.tickets.map((t) =>
            t.id === ticketId
              ? { ...t, reconciled: !newValue, reconciledAt: !newValue ? t.reconciledAt : null }
              : t,
          ),
          stats: {
            ...prev.stats,
            reconciledCount: prev.stats.reconciledCount + (newValue ? -1 : 1),
          },
        };
      });
      setReconcileError('Failed to update reconciliation status. Please try again.');
    }
  };

  if (loading) {
    return <div style={styles.loading}>Loading ticket inventory...</div>;
  }

  if (error) {
    return <div style={styles.error}>{error}</div>;
  }

  if (!data) {
    return null;
  }

  const { stats } = data;

  return (
    <div style={styles.container}>
      {/* Ticket Deletion Impact Modal */}
      {deleteImpact && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => setDeleteImpact(null)}>
          <div style={{ backgroundColor: '#fff', borderRadius: '12px', width: '520px', maxWidth: '94vw', maxHeight: '85vh', overflow: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }} onClick={e => e.stopPropagation()}>
            <div style={{ background: 'linear-gradient(135deg, #dc2626, #991b1b)', padding: '14px 20px', borderRadius: '12px 12px 0 0', color: '#fff' }}>
              <div style={{ fontWeight: 700, fontSize: '1rem' }}>🗑️ Delete Ticket — Impact Analysis</div>
              <div style={{ fontSize: '0.75rem', opacity: 0.85, marginTop: '2px' }}>{deleteImpact.ticketNumber} • {deleteImpact.guestName}</div>
            </div>
            <div style={{ padding: '20px' }}>
              {/* Ticket Info */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '16px', fontSize: '0.82rem' }}>
                <div><strong>Tier:</strong> {deleteImpact.tierName}</div>
                <div><strong>Order:</strong> {deleteImpact.orderRef || '—'}</div>
                <div><strong>Purchaser:</strong> {deleteImpact.purchaserName || '—'}</div>
                <div><strong>Meal:</strong> {deleteImpact.mealOption || '—'}</div>
              </div>

              {/* Revenue Impact */}
              {deleteImpact.revenueImpact > 0 && (
                <div style={{ background: '#fef3c7', border: '1px solid #fbbf24', borderRadius: '8px', padding: '10px 14px', marginBottom: '16px', fontSize: '0.82rem' }}>
                  <strong style={{ color: '#92400e' }}>Revenue Impact:</strong> -${deleteImpact.revenueImpact.toFixed(2)} will be removed from total revenue
                </div>
              )}

              {/* Dependencies */}
              <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>
                Dependencies ({deleteImpact.dependencies.length})
              </div>
              {deleteImpact.dependencies.length === 0 ? (
                <div style={{ fontSize: '0.82rem', color: '#065f46', padding: '8px 0' }}>No dependencies — safe to delete.</div>
              ) : (
                <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
                  {deleteImpact.dependencies.map((dep: any, i: number) => (
                    <div key={i} style={{ display: 'flex', gap: '10px', padding: '8px 12px', borderBottom: i < deleteImpact.dependencies.length - 1 ? '1px solid #f3f4f6' : 'none', fontSize: '0.8rem' }}>
                      <span style={{ fontWeight: 600, color: '#dc2626', minWidth: '110px', flexShrink: 0 }}>{dep.type}</span>
                      <span style={{ color: '#374151' }}>{dep.detail}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Summary badges */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '14px' }}>
                {deleteImpact.summary.hasGuestRecord && <span style={{ padding: '3px 8px', borderRadius: '10px', fontSize: '0.7rem', fontWeight: 600, backgroundColor: '#fee2e2', color: '#991b1b' }}>Guest Record</span>}
                {deleteImpact.summary.hasRegistration && <span style={{ padding: '3px 8px', borderRadius: '10px', fontSize: '0.7rem', fontWeight: 600, backgroundColor: '#fee2e2', color: '#991b1b' }}>Registration</span>}
                {deleteImpact.summary.hasSyntheticUser && <span style={{ padding: '3px 8px', borderRadius: '10px', fontSize: '0.7rem', fontWeight: 600, backgroundColor: '#fee2e2', color: '#991b1b' }}>User Account</span>}
                {deleteImpact.summary.seatCount > 0 && <span style={{ padding: '3px 8px', borderRadius: '10px', fontSize: '0.7rem', fontWeight: 600, backgroundColor: '#fef3c7', color: '#92400e' }}>{deleteImpact.summary.seatCount} Seat(s)</span>}
                {deleteImpact.summary.hasCheckIn && <span style={{ padding: '3px 8px', borderRadius: '10px', fontSize: '0.7rem', fontWeight: 600, backgroundColor: '#fef3c7', color: '#92400e' }}>Check-In</span>}
                {deleteImpact.summary.hasMealSelection && <span style={{ padding: '3px 8px', borderRadius: '10px', fontSize: '0.7rem', fontWeight: 600, backgroundColor: '#dbeafe', color: '#1e40af' }}>Meal</span>}
                {deleteImpact.summary.hasBetaTester && <span style={{ padding: '3px 8px', borderRadius: '10px', fontSize: '0.7rem', fontWeight: 600, backgroundColor: '#f3e8ff', color: '#7c3aed' }}>Beta Tester</span>}
              </div>

              {/* Action buttons */}
              <div style={{ display: 'flex', gap: '10px', marginTop: '20px', justifyContent: 'flex-end' }}>
                <button onClick={() => setDeleteImpact(null)} style={{ padding: '8px 16px', borderRadius: '6px', border: '1px solid #d1d5db', backgroundColor: '#f9fafb', color: '#374151', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
                <button onClick={confirmDeleteTicket} disabled={deletingTicket} style={{ padding: '8px 16px', borderRadius: '6px', border: 'none', backgroundColor: '#dc2626', color: '#fff', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', opacity: deletingTicket ? 0.6 : 1 }}>
                  {deletingTicket ? 'Deleting...' : 'Confirm Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sub-Navigation Bar */}
      <nav style={styles.subNav} aria-label="Ticket inventory views">
        {TICKET_SUB_VIEWS
          .filter((view) => !view.organizerOnly || (user && ['Admin', 'Organizer'].includes(user.role)))
          .map((view) => (
          <button
            key={view.key}
            style={activeView === view.key ? styles.subTabActive : styles.subTab}
            onClick={() => setActiveView(view.key)}
            aria-current={activeView === view.key ? 'page' : undefined}
          >
            {view.label}
          </button>
        ))}
      </nav>

      {/* Dashboard View */}
      {activeView === 'dashboard' && (
        <>
          {/* Summary Stats */}
          <div style={styles.statsPanel}>
            <div style={styles.statCard}>
              <div style={styles.statLabel}>Total Tickets</div>
              <div style={styles.statValue}>{stats.totalCount}</div>
            </div>
            <div style={styles.statCard}>
              <div style={styles.statLabel}>Assigned</div>
              <div style={styles.statValue}>{stats.assignedCount}</div>
            </div>
            <div style={styles.statCard}>
              <div style={styles.statLabel}>Unassigned</div>
              <div style={styles.statValue}>{stats.unassignedCount}</div>
            </div>
            <div style={styles.statCard}>
              <div style={styles.statLabel}>Reconciled</div>
              <div style={styles.statValue}>{stats.reconciledCount}</div>
            </div>
            <div style={styles.statCard}>
              <div style={styles.statLabel}>Total Revenue</div>
              <div style={styles.statValue}>{formatCurrency(stats.totalRevenue)}</div>
            </div>
          </div>

          {/* Per-Tier Breakdown */}
          {stats.byTier.length > 0 && (
            <>
              <div style={styles.sectionTitle}>Revenue by Tier</div>
              <div style={styles.tierGrid}>
                {stats.byTier.map((tier) => (
                  <div key={tier.tierName} style={styles.tierCard}>
                    <div style={styles.tierName}>{tier.tierName}</div>
                    <div style={styles.tierDetail}>
                      {tier.count} ticket{tier.count !== 1 ? 's' : ''} &middot;{' '}
                      {formatCurrency(tier.revenue)}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Complimentary Tickets */}
          {stats.complimentary && stats.complimentary.ticketCount > 0 && (
            <div style={{ marginTop: '1rem', padding: '0.75rem 1rem', backgroundColor: '#eff6ff', border: '1px solid #93c5fd', borderRadius: '8px' }}>
              <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#1e40af', marginBottom: '0.4rem' }}>🎁 Complimentary Tickets</div>
              <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
                <div>
                  <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#1e40af' }}>{stats.complimentary.ticketCount}</div>
                  <div style={{ fontSize: '0.72rem', color: '#3b82f6' }}>comp tickets</div>
                </div>
                <div>
                  <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#1e40af' }}>{formatCurrency(stats.complimentary.faceValue)}</div>
                  <div style={{ fontSize: '0.72rem', color: '#3b82f6' }}>face value (excluded from revenue)</div>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Tickets View */}
      {activeView === 'tickets' && (
        <>
          {/* Bulk Generate Form */}
          <div style={styles.formSection}>
            <h3 style={styles.formTitle}>Bulk Generate Tickets</h3>
            <form onSubmit={handleBulkGenerate}>
              <div style={styles.formRow}>
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>Tier</label>
                  <select
                    style={styles.formSelect}
                    value={selectedTierId}
                    onChange={(e) => setSelectedTierId(e.target.value)}
                    disabled={submitting}
                  >
                    <option value="">Select a tier...</option>
                    {tiers.map((tier) => (
                      <option key={tier.id} value={tier.id}>
                        {tier.name} ({formatCurrency(tier.price)})
                      </option>
                    ))}
                  </select>
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>Quantity (1–500)</label>
                  <input
                    type="number"
                    style={styles.formInput}
                    value={quantity}
                    onChange={(e) => setQuantity(e.target.value)}
                    min={1}
                    max={500}
                    placeholder="100"
                    disabled={submitting}
                  />
                </div>
                <button
                  type="submit"
                  style={
                    submitting ? styles.formButtonDisabled : styles.formButton
                  }
                  disabled={submitting}
                >
                  {submitting ? 'Generating...' : 'Generate'}
                </button>
              </div>
            </form>
            {successMessage && (
              <div style={styles.successMessage}>{successMessage}</div>
            )}
            {formError && <div style={styles.formError}>{formError}</div>}
          </div>

          {/* Ticket Table */}
          <div style={styles.tableContainer}>
            <div style={styles.collapseHeader} onClick={() => setTicketsCollapsed(!ticketsCollapsed)} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setTicketsCollapsed(!ticketsCollapsed); }} aria-expanded={!ticketsCollapsed} aria-label="Toggle ticket list">
              <div style={styles.sectionTitle}>All Tickets ({data.tickets.length})</div>
              <span style={{ ...styles.collapseArrow, transform: ticketsCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)' }}>▼</span>
            </div>

            {!ticketsCollapsed && (
              <>
                {/* Filter/Group Toolbar */}
                <div style={styles.ticketToolbar}>
                  <input
                    style={{ ...styles.toolbarSelect, minWidth: '180px' }}
                    type="text"
                    placeholder="Search ticket # or name..."
                    value={ticketSearch}
                    onChange={(e) => setTicketSearch(e.target.value)}
                    aria-label="Search by ticket number or guest name"
                  />
                  <select
                    style={styles.toolbarSelect}
                    value={tierFilter}
                    onChange={(e) => setTierFilter(e.target.value)}
                    aria-label="Filter by tier"
                  >
                    <option value="">All Tiers</option>
                    {Array.from(new Set(data.tickets.map(t => t.tierName))).sort().map((name) => (
                      <option key={name} value={name}>{name}</option>
                    ))}
                  </select>
                  <select
                    style={styles.toolbarSelect}
                    value={createdViaFilter}
                    onChange={(e) => setCreatedViaFilter(e.target.value)}
                    aria-label="Filter by creation method"
                  >
                    <option value="">All Sources</option>
                    {Array.from(new Set(data.tickets.map(t => t.createdVia || 'Unknown'))).sort().map((v) => (
                      <option key={v} value={v}>{v}</option>
                    ))}
                  </select>
                  <button
                    style={groupByTier ? styles.toolbarToggleActive : styles.toolbarToggle}
                    onClick={() => setGroupByTier(!groupByTier)}
                    aria-pressed={groupByTier}
                  >
                    Group by Tier
                  </button>
                </div>

                {reconcileError && (
                  <div style={styles.reconcileError}>{reconcileError}</div>
                )}

                {(() => {
                  let filtered = tierFilter
                    ? data.tickets.filter(t => t.tierName === tierFilter)
                    : [...data.tickets];
                  if (createdViaFilter) {
                    filtered = filtered.filter(t => (t.createdVia || 'Unknown') === createdViaFilter);
                  }
                  if (ticketSearch.trim()) {
                    const q = ticketSearch.trim().toLowerCase();
                    filtered = filtered.filter(t => {
                      const tktStr = `TKT-${String(t.ticketNumber).padStart(3, '0')}`.toLowerCase();
                      const name = (t.guestName || '').toLowerCase();
                      return tktStr.includes(q) || name.includes(q) || String(t.ticketNumber).includes(q);
                    });
                  }

                  if (filtered.length === 0) {
                    return (
                      <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280', fontSize: '0.85rem' }}>
                        {data.tickets.length === 0
                          ? 'No tickets found. Use the bulk generate form to create tickets.'
                          : 'No tickets match the selected tier filter.'}
                      </div>
                    );
                  }

                  if (groupByTier) {
                    const grouped = filtered.reduce<Record<string, typeof filtered>>((acc, ticket) => {
                      if (!acc[ticket.tierName]) acc[ticket.tierName] = [];
                      acc[ticket.tierName].push(ticket);
                      return acc;
                    }, {});

                    return Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b)).map(([tierName, tickets]) => (
                      <div key={tierName} style={{ marginBottom: '1rem' }}>
                        <div style={styles.tierGroupHeader}>{tierName} ({tickets.length})</div>
                        <table style={styles.table}>
                          <thead style={styles.tableHeader}>
                            <tr>
                              <th style={styles.th}>Ticket #</th>
                              <th style={styles.th}>Price</th>
                              <th style={styles.th}>Status</th>
                              <th style={styles.th}>Assigned To</th>
                              <th style={styles.th}>Created Via</th>
                              <th style={styles.th}>Reconciled</th>
                            </tr>
                          </thead>
                          <tbody>
                            {tickets.map((ticket) => (
                              <tr key={ticket.id}>
                                <td style={styles.td}>TKT-{String(ticket.ticketNumber).padStart(3, '0')}</td>
                                <td style={styles.td}>{formatCurrency(ticket.priceAtPurchase)}</td>
                                <td style={styles.td}>{ticket.status}</td>
                                <td style={styles.td}>
                                  {ticket.assignedTo ? ticket.assignedTo : <span style={styles.unassignedBadge}>Unassigned</span>}
                                </td>
                                <td style={styles.td}>
                                  <input
                                    type="checkbox"
                                    style={styles.reconcileCheckbox}
                                    checked={ticket.reconciled}
                                    onChange={() => handleReconcileToggle(ticket.id, !ticket.reconciled)}
                                    aria-label={`Mark ticket TKT-${String(ticket.ticketNumber).padStart(3, '0')} as ${ticket.reconciled ? 'not reconciled' : 'reconciled'}`}
                                  />
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ));
                  }

                  return (
                    <table style={styles.table}>
                      <thead style={styles.tableHeader}>
                        <tr>
                          <th style={styles.th}>Ticket #</th>
                          <th style={styles.th}>Tier Name</th>
                          <th style={styles.th}>Price</th>
                          <th style={styles.th}>Status</th>
                          <th style={styles.th}>Assigned To</th>
                          <th style={styles.th}>Created Via</th>
                          <th style={styles.th}>Reconciled</th>
                          <th style={styles.th}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {filtered.map((ticket) => (
                          <tr key={ticket.id}>
                            <td style={styles.td}>TKT-{String(ticket.ticketNumber).padStart(3, '0')}</td>
                            <td style={styles.td}>
                              <select
                                value={tiers.find((t) => t.name === ticket.tierName)?.id || ''}
                                onChange={(e) => handleChangeTier(ticket.id, e.target.value)}
                                style={{ fontSize: '0.8rem', padding: '2px 4px', border: '1px solid #d0d8d3', borderRadius: '4px', background: '#fff' }}
                                aria-label={`Change tier for TKT-${String(ticket.ticketNumber).padStart(3, '0')}`}
                              >
                                {tiers.map((t) => (
                                  <option key={t.id} value={t.id}>{t.name}</option>
                                ))}
                              </select>
                            </td>
                            <td style={styles.td}>{formatCurrency(ticket.priceAtPurchase)}</td>
                            <td style={styles.td}>{ticket.status}</td>
                            <td style={styles.td}>
                              {ticket.assignedTo ? (
                                <span
                                  style={{ cursor: 'pointer', borderBottom: '1px dashed #999' }}
                                  title="Click to rename"
                                  onClick={() => {
                                    const newName = window.prompt('Enter new guest name:', ticket.assignedTo || '');
                                    if (newName && newName.trim() && newName.trim() !== ticket.assignedTo) {
                                      handleChangeGuestName(ticket.id, newName.trim());
                                    }
                                  }}
                                >{ticket.assignedTo}</span>
                              ) : <span style={styles.unassignedBadge}>Unassigned</span>}
                            </td>
                            <td style={styles.td}>{ticket.createdVia || '—'}</td>
                            <td style={styles.td}>
                              <input
                                type="checkbox"
                                style={styles.reconcileCheckbox}
                                checked={ticket.reconciled}
                                onChange={() => handleReconcileToggle(ticket.id, !ticket.reconciled)}
                                aria-label={`Mark ticket TKT-${String(ticket.ticketNumber).padStart(3, '0')} as ${ticket.reconciled ? 'not reconciled' : 'reconciled'}`}
                              />
                            </td>
                            <td style={styles.td}>
                              <button
                                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 6px', fontSize: '0.9rem', borderRadius: '4px', lineHeight: 1 }}
                                onClick={() => handleDeleteTicket(ticket.id, ticket.ticketNumber, ticket.assignedTo)}
                                title="Delete ticket"
                                aria-label={`Delete ticket TKT-${String(ticket.ticketNumber).padStart(3, '0')}`}
                              >🗑️</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  );
                })()}
              </>
            )}
          </div>
        </>
      )}

      {/* Orders View */}
      {activeView === 'orders' && (
        <>
          <CreateOrderForm tiers={tiers} onOrderCreated={() => { fetchInventory(); setOrdersRefreshKey((k) => k + 1); }} />
          <OrdersList onOrderChanged={fetchInventory} refreshTrigger={ordersRefreshKey} />
        </>
      )}

      {/* Import View */}
      {activeView === 'import' && (
        <ImportOrdersSection />
      )}

      {/* Order/Ticket Audit View */}
      {activeView === 'audit' && <OrderTicketAudit />}

      {/* Data Management View */}
      {activeView === 'data-management' && (
        <DataManagementTab user={user} />
      )}

      {/* Reconciliation View */}
      {activeView === 'reconciliation' && (
        <ReconciliationTab />
      )}
    </div>
  );
}
